import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Typography, TextField,
  ToggleButtonGroup, ToggleButton, Alert, CircularProgress, List, ListItem, ListItemText,
} from '@mui/material';
import axios from '../../../config/axiosInstance';
import { useSnackbar } from 'notistack';
import { fmtDate, errorMessage } from './staffingUtils';

const todayIso = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

// Aplica la dotación vigente a los calendarios ABIERTOS de la estación: recalcula el
// esperado (actual/esperado) de los días elegidos. Los calendarios cerrados no se tocan.
export default function StaffingRecalcDialog({ open, onClose, stationId, initialRange, onApplied }) {
  const { enqueueSnackbar } = useSnackbar();
  const [mode, setMode] = useState('from');
  const [dateFrom, setDateFrom] = useState(todayIso());
  const [dateTo, setDateTo] = useState('');
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDateFrom(initialRange?.date_from || todayIso());
    setDateTo(initialRange?.date_to || '');
    setMode(initialRange?.date_to ? 'period' : 'from');
    setPreview(null);
  }, [open, initialRange]);

  const rangeReady = Boolean(dateFrom) && (mode === 'from' || (Boolean(dateTo) && dateTo >= dateFrom));
  const body = { date_from: dateFrom, date_to: mode === 'period' ? dateTo : null };

  useEffect(() => {
    if (!open || !rangeReady) { setPreview(null); return undefined; }
    let cancelled = false;
    setLoadingPreview(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.post(`/station/${stationId}/staffing/recalc/preview`, body);
        if (!cancelled) setPreview(data);
      } catch (e) {
        if (!cancelled) {
          setPreview(null);
          enqueueSnackbar(errorMessage(e, 'No se pudo calcular la vista previa.'), { variant: 'error' });
        }
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stationId, dateFrom, dateTo, mode, rangeReady]);

  const handleApply = async () => {
    setApplying(true);
    try {
      const { data } = await axios.post(`/station/${stationId}/staffing/recalc`, body);
      enqueueSnackbar(
        data.days_changed > 0
          ? `Se actualizaron ${data.days_changed} día(s) en ${data.calendars_updated} calendario(s).`
          : 'Los calendarios ya estaban al día.',
        { variant: 'success' }
      );
      onApplied?.(data);
      onClose();
    } catch (e) {
      enqueueSnackbar(errorMessage(e, 'No se pudo recalcular los calendarios.'), { variant: 'error' });
    } finally {
      setApplying(false);
    }
  };

  const nothingToDo = preview && preview.total_calendars === 0;

  return (
    <Dialog open={open} onClose={applying ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Aplicar a calendarios abiertos</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <ToggleButtonGroup
            exclusive size="small" fullWidth value={mode}
            onChange={(_e, value) => value && setMode(value)}
          >
            <ToggleButton value="from">Desde una fecha en adelante</ToggleButton>
            <ToggleButton value="period">Un período</ToggleButton>
          </ToggleButtonGroup>

          <Stack direction="row" spacing={2}>
            <TextField
              type="date" size="small" fullWidth label={mode === 'from' ? 'Desde' : 'Desde'}
              value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }}
            />
            {mode === 'period' && (
              <TextField
                type="date" size="small" fullWidth label="Hasta" value={dateTo}
                onChange={(e) => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }}
                error={Boolean(dateTo) && dateTo < dateFrom}
              />
            )}
          </Stack>

          {loadingPreview && <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={16} /><Typography variant="body2">Calculando…</Typography></Stack>}

          {preview && !loadingPreview && (
            <>
              {nothingToDo ? (
                <Alert severity="info">No hay calendarios abiertos en ese rango.</Alert>
              ) : (
                <Alert severity="info">
                  Se recalcularán <b>{preview.total_days}</b> día(s) en <b>{preview.total_calendars}</b> calendario(s) abierto(s).
                </Alert>
              )}
              {preview.calendars.length > 0 && (
                <List dense disablePadding>
                  {preview.calendars.map((c) => (
                    <ListItem key={c.calendar_id} disableGutters>
                      <ListItemText
                        primary={c.position_name || `Área ${c.position_id}`}
                        secondary={`${fmtDate(c.start_date)} al ${fmtDate(c.end_date)} · ${c.days} día(s) en el rango`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
              {preview.closed_calendars_untouched > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {preview.closed_calendars_untouched} calendario(s) cerrado(s) en ese rango no se modifican.
                </Typography>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={applying}>Ahora no</Button>
        <Button variant="contained" onClick={handleApply} disabled={!rangeReady || loadingPreview || nothingToDo || applying}>
          Aplicar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
